// El PLAN del Catálogo con el agente (ADR-2026-10-13-catalogo-con-el-agente): lo que el agente
// arma (tomas + estilo), lo que el escritor del backend llena (un prompt por producto y toma) y
// lo que la generación va marcando (lanzado, generada, fallo). Vive en `agent_chats.lienzo` de la
// sesión y acá está todo lo que se calcula sobre él sin React ni red: sincronizar ítems con las
// tomas, qué falta para generar, el costo, cómo se parte en lotes de ≤ 100 imágenes para
// `/v1/agent/lote` y cómo cada fila que llega vuelve a su ítem. Puro.
import { normalizarFicha, type FichaDeProducto } from '../../../../lib/fichaDeProducto';
import { normalizarMedidas, resumenMedidas, tieneMedidasCompletas, type Medidas } from '../../../../lib/medidas';
import { creditosPorResolucion, normalizarResolucion, RESOLUCION_DEFAULT, type ResolucionImagen } from '../../../../lib/resolucionImagen';
import { DEFAULT_FORMAT_ID, FORMAT_OPTIONS_V2 } from '../../mockData';

export const ICONOS_DE_TOMA = [
  'packshot',
  'ambiente',
  'detalle',
  'medidas',
  'en-uso',
  'conjunto',
  'frente',
  'lateral',
  'superior',
  'materiales',
  'abierto',
  'otro',
] as const;
export type IconoDeToma = (typeof ICONOS_DE_TOMA)[number];

export const MAX_TOMAS = 8;
export const MAX_PRODUCTOS = 100;
export const MAX_CANTIDAD_TOMA = 3;
/** Espejo de `MAX_IMAGENES_LOTE` del backend (`services/ads/loteDelAgente.ts`). */
export const MAX_IMAGENES_POR_LOTE = 100;
export const MAX_ESTILO = 1500;
export const MAX_QUE_MUESTRA = 600;
export const MAX_TITULO_TOMA = 60;

export const TAREAS_DE_IMAGEN = ['crear', 'editar', 'componer', 'explorar', 'grafica', 'cine'] as const;
export type TareaDeImagen = (typeof TAREAS_DE_IMAGEN)[number];

/** Los fondos de la infografía de medidas (los de la plantilla del backend). */
export const COLORES_DE_INFOGRAFIA = ['blanco', 'grafito', 'beige'] as const;
export type ColorDeInfografia = (typeof COLORES_DE_INFOGRAFIA)[number];
/** Cuántas características de la ficha van como etiquetas en la infografía. */
export const MAX_PILLS_DE_INFOGRAFIA = 4;
/** Una etiqueta es una línea corta en una pill: "Torque máximo: 10 N·m". */
export const MAX_LARGO_ETIQUETA = 60;

export interface TomaDelPlan {
  /** kebab, estable ("packshot-frente"): es el `toma_id` del escritor y la clave del ítem. */
  id: string;
  titulo: string;
  icono: IconoDeToma;
  /** La instrucción de la toma para el escritor (≤ 600). */
  queMuestra: string;
  /** `'todos'` o ids de variante. */
  aplicaA: 'todos' | string[];
  /** Imágenes por producto en esta toma. */
  cantidad: 1 | 2 | 3;
  fondoBlanco?: boolean;
  luzFondoBlanco?: 'difusa' | 'dura';
  tarea?: TareaDeImagen;
  /** Ids de `referencias` del plan que viajan con esta toma. */
  referencias?: string[];
  /**
   * La INFOGRAFÍA de medidas (ADR-2026-10-14-el-agente-sabe-de-hogar,
   * ADR-2026-10-14-la-infografia-es-un-prompt-mas): el escritor no la redacta con el modelo,
   * llena la plantilla de ficha técnica del catálogo viejo con las medidas reales (exige
   * medidas) y, si `pillsDeLaFicha` (default), hasta 4 datos de la ficha como etiquetas.
   * `estiloGrafico` (colores, tipografía de la marca) va en la plantilla. El resultado es un
   * prompt más: editable, y viaja con las fotos del producto y las referencias de la toma.
   */
  infografia?: true;
  pillsDeLaFicha?: boolean;
  colorInfografia?: ColorDeInfografia;
  estiloGrafico?: string;
}

export const esInfografia = (toma: TomaDelPlan | undefined): boolean => !!toma?.infografia;

const capitalizar = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);

const sinAcentos = (s: string): string => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/** Claves que identifican o nombran el producto: nunca le dicen nada al comprador en una lámina. */
const CLAVES_QUE_NO_SON_ETIQUETA = new Set([
  'medidas', 'marca', 'modelo', 'codigo', 'cod', 'codigo de barras', 'sku', 'ean', 'gtin', 'upc', 'isbn',
  'referencia', 'ref', 'articulo', 'art', 'tipo', 'tipo de producto', 'categoria', 'nombre', 'producto',
]);

/**
 * ¿Sirve como etiqueta de la ficha? No la marca, el modelo ni un código, ni lo que el nombre ya
 * dice ("Taladro" en "Taladro rojo Pektra"): eso salió en las primeras fichas porque se tomaban
 * los 4 primeros datos tal como venían (ADR-2026-10-19-las-etiquetas-de-la-ficha-son-las-que-importan).
 */
export function esDatoParaEtiqueta(campo: { clave: string; valor: string }, nombreDelProducto: string): boolean {
  const valor = sinAcentos(campo.valor);
  if (!valor) return false;
  if (CLAVES_QUE_NO_SON_ETIQUETA.has(sinAcentos(campo.clave))) return false;
  return !sinAcentos(nombreDelProducto).includes(valor);
}

/**
 * Las etiquetas de la infografía: las que eligió el agente para ESE producto (`etiquetas`) o, si no
 * eligió, hasta 4 datos de la ficha que sirvan (sin medidas, marca, modelo, códigos ni lo obvio).
 */
export function pillsDeLaFicha(producto: ProductoDelPlan, toma: TomaDelPlan): string[] {
  if (toma.pillsDeLaFicha === false) return [];
  if (producto.etiquetas?.length) return producto.etiquetas.slice(0, MAX_PILLS_DE_INFOGRAFIA);
  return producto.ficha.campos
    .filter((c) => esDatoParaEtiqueta(c, producto.nombre))
    .slice(0, MAX_PILLS_DE_INFOGRAFIA)
    .map((c) => (c.clave.trim() ? `${capitalizar(c.clave.trim())}: ${c.valor.trim()}` : c.valor.trim()));
}


export interface DatoDeProducto {
  clave: string;
  valor: string;
}

export type EstadoDeItem = 'sin-prompt' | 'escrito' | 'generando' | 'generada' | 'fallo';
const ESTADOS: readonly EstadoDeItem[] = ['sin-prompt', 'escrito', 'generando', 'generada', 'fallo'];

export interface ItemDelPlan {
  /** `${varianteId}__${tomaId}`. */
  id: string;
  tomaId: string;
  prompt: string;
  /** Editado a mano o por `editar_prompt`: el escritor no lo pisa. */
  fijado: boolean;
  estado: EstadoDeItem;
  motivo?: string;
  executionId?: string;
  imageIds?: string[];
  /** Lo que declaró el escritor para ESTE ítem (eligen el motor en el backend); la toma es el respaldo. */
  tarea?: TareaDeImagen;
  pielExpuesta?: boolean;
  /** Edición pendiente de la foto ya salida (ADR-2026-10-14-el-agente-edita-el-catalogo-en-masa):
   *  Generar la manda a editar con la foto de la tarjeta como base, en vez de rehacerla. */
  edicion?: EdicionPendiente;
  /** Rehacer pendiente: Generar la vuelve a generar con su prompt. */
  rehacer?: true;
  /** Las fotos que tenía al lanzarse: vuelven si lo nuevo no sale. */
  previas?: string[];
  /** Lo último que se lanzó fue una edición: la foto editada reemplaza sólo a la de la tarjeta. */
  fueEdicion?: true;
  /** Cuándo se lanzó (ms): la barra del recuadro de carga sigue ese reloj, no el del componente. */
  lanzadoEn?: number;
}

export interface EdicionPendiente {
  instruccion: string;
  /** La foto que se edita (la de la tarjeta, `imageIds[0]` al marcar). */
  baseImageId: string;
}

export const MAX_INSTRUCCION_EDICION = 600;

/** El prompt de una edición: la foto de referencia manda y sólo cambia lo pedido. */
export const promptDeEdicion = (instruccion: string): string =>
  `Editá la foto de referencia "La foto a editar": ${instruccion.trim()}. Mantené todo lo demás exactamente igual: el producto (fiel al producto real de las otras referencias), el encuadre, la cámara, la luz, el fondo y la composición; cambiá sólo lo pedido.`;

export interface ProductoDelPlan {
  varianteId: string;
  nombre: string;
  portadaUrl?: string;
  medidas: Medidas | null;
  ficha: FichaDeProducto;
  /** Lo que la persona dijo de ESTE producto. */
  notas?: string;
  /** Las etiquetas de su ficha de medidas que eligió el agente ("Torque: 10 N·m"); sin esto,
   *  `pillsDeLaFicha` las saca de la ficha (ADR-2026-10-19-las-etiquetas-de-la-ficha-son-las-que-importan). */
  etiquetas?: string[];
  items: ItemDelPlan[];
}

export interface ReferenciaDelPlan {
  id: string;
  etiqueta: string;
  storagePath: string;
  bucket?: string;
}

export interface LoteLanzado {
  jobId: string;
  executionIds: string[];
  itemIds: string[];
  total: number;
  terminado: boolean;
}

export interface PlanDeCatalogo {
  version: 1;
  titulo: string;
  pedido: string;
  estilo: string;
  /** Slug de `FORMAT_OPTIONS_V2`; global. */
  formato: string;
  resolucion: ResolucionImagen;
  tomas: TomaDelPlan[];
  productos: ProductoDelPlan[];
  referencias: ReferenciaDelPlan[];
  lotes: LoteLanzado[];
  /** El pedido de la portada ya se le mandó al agente (ADR-2026-10-14-el-pedido-llega-al-agente-desde-el-plan). */
  pedidoEnviado?: boolean;
}

export const idDeItem = (varianteId: string, tomaId: string): string => `${varianteId}__${tomaId}`;

/** El slug del formato → la proporción que recibe el backend ("4:5"). Sale del nombre de la opción. */
export function aspectDelFormato(slug: string | null | undefined): string {
  const opt = FORMAT_OPTIONS_V2.find((f) => f.id === slug);
  const m = opt?.name.match(/\((\d+:\d+)\)/);
  return m?.[1] ?? '4:5';
}

export interface ProductoParaElPlan {
  varianteId: string;
  nombre: string;
  portadaUrl?: string;
  medidas: Medidas | null;
  ficha: FichaDeProducto;
}

const tituloDeHoy = (): string =>
  `Catálogo ${new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}`;

/** Suma una imagen como referencia del plan o, si ya está, le cambia sólo la etiqueta: así el
 *  agente marca una pieza de la marca como "Plantilla: …" aunque haya llegado a mitad del chat
 *  (ADR-2026-10-15-la-plantilla-de-marca). Una etiqueta vacía no pisa la que había. */
export function ponerReferencia(plan: PlanDeCatalogo, ref: ReferenciaDelPlan): PlanDeCatalogo {
  const etiqueta = ref.etiqueta.trim().slice(0, 120);
  const actual = plan.referencias.find((r) => r.id === ref.id);
  if (actual) {
    if (!etiqueta || etiqueta === actual.etiqueta) return plan;
    return { ...plan, referencias: plan.referencias.map((r) => (r.id === ref.id ? { ...r, etiqueta } : r)) };
  }
  return { ...plan, referencias: [...plan.referencias, { ...ref, etiqueta: etiqueta || `Referencia ${plan.referencias.length + 1}` }] };
}

export function planVacio(productos: ProductoParaElPlan[], referencias: ReferenciaDelPlan[], pedido: string): PlanDeCatalogo {
  return {
    version: 1,
    titulo: tituloDeHoy(),
    pedido,
    estilo: '',
    formato: DEFAULT_FORMAT_ID,
    resolucion: RESOLUCION_DEFAULT,
    tomas: [],
    productos: productos.map((p) => ({
      varianteId: p.varianteId,
      nombre: p.nombre,
      ...(p.portadaUrl ? { portadaUrl: p.portadaUrl } : {}),
      medidas: p.medidas,
      ficha: p.ficha,
      items: [],
    })),
    referencias,
    lotes: [],
  };
}

/**
 * Si la sesión tiene que mandarle el pedido al agente: hay pedido, todavía no hay tomas, el
 * chat no tiene mensajes y no se mandó antes. Lo decide el PLAN guardado y no el estado de la
 * navegación, así funciona aunque se recargue o el chat y el plan carguen en otro orden.
 */
export const debeEnviarPedido = (plan: PlanDeCatalogo, mensajesEnElChat: number): boolean =>
  !!plan.pedido.trim() && !plan.pedidoEnviado && plan.tomas.length === 0 && mensajesEnElChat === 0;

export const tomaAplica = (toma: TomaDelPlan, varianteId: string): boolean =>
  toma.aplicaA === 'todos' || toma.aplicaA.includes(varianteId);

/**
 * Un ítem por (producto, toma aplicable), en el orden de las tomas. Conserva los existentes
 * (prompt, fijado, estado) y borra los de tomas que ya no están o ya no aplican.
 */
export function sincronizarItems(plan: PlanDeCatalogo): PlanDeCatalogo {
  return {
    ...plan,
    productos: plan.productos.map((p) => {
      const previos = new Map(p.items.map((i) => [i.id, i]));
      const items: ItemDelPlan[] = plan.tomas
        .filter((t) => tomaAplica(t, p.varianteId))
        .map((t) => {
          const id = idDeItem(p.varianteId, t.id);
          return previos.get(id) ?? { id, tomaId: t.id, prompt: '', fijado: false, estado: 'sin-prompt' };
        });
      return { ...p, items };
    }),
  };
}

/**
 * Sacar UNA toma de UN producto (el "sacar" del panel del ítem): la toma deja de aplicar a ese
 * producto (de `'todos'` pasa a la lista de los demás) y su ítem se va. Si era el último
 * producto de la toma, la toma se va entera. Un producto o una toma que no están, el mismo plan.
 */
export function sacarProductoDeToma(plan: PlanDeCatalogo, varianteId: string, tomaId: string): PlanDeCatalogo {
  const toma = plan.tomas.find((t) => t.id === tomaId);
  if (!toma || !plan.productos.some((p) => p.varianteId === varianteId)) return plan;
  const aplicaba = toma.aplicaA === 'todos' ? plan.productos.map((p) => p.varianteId) : toma.aplicaA;
  if (!aplicaba.includes(varianteId)) return plan;
  const quedan = aplicaba.filter((id) => id !== varianteId);
  const tomas = quedan.length === 0 ? plan.tomas.filter((t) => t.id !== tomaId) : plan.tomas.map((t) => (t.id === tomaId ? { ...t, aplicaA: quedan } : t));
  return sincronizarItems({ ...plan, tomas });
}

export interface ProductosSumados {
  plan: PlanDeCatalogo;
  /** Los que entraron, en el orden pedido. */
  sumados: string[];
  /** Ya estaban en el plan: no se tocan. */
  yaEstaban: string[];
  /** No entraron por el tope de productos. */
  sinLugar: string[];
}

/**
 * Sumar productos a un catálogo ya armado (ADR-2026-10-15-el-agente-suma-productos-al-catalogo).
 * Van al final y reciben las tomas de `tomaIds` (sin él, TODAS las del plan, como los que ya
 * estaban); una toma que no está en la lista y aplicaba a 'todos' pasa a la lista de los de
 * antes, así el nuevo no la hereda. Sus ítems quedan "sin prompt": los escribe `escribir_prompts`.
 */
export function sumarProductos(
  plan: PlanDeCatalogo,
  nuevos: ProductoParaElPlan[],
  tomaIds?: readonly string[],
): ProductosSumados {
  const presentes = new Set(plan.productos.map((p) => p.varianteId));
  const yaEstaban: string[] = [];
  const candidatos: ProductoParaElPlan[] = [];
  for (const p of nuevos) {
    if (presentes.has(p.varianteId)) yaEstaban.push(p.varianteId);
    else if (!candidatos.some((c) => c.varianteId === p.varianteId)) candidatos.push(p);
  }
  const lugar = Math.max(0, MAX_PRODUCTOS - plan.productos.length);
  const entran = candidatos.slice(0, lugar);
  const sinLugar = candidatos.slice(lugar).map((p) => p.varianteId);
  if (entran.length === 0) return { plan, sumados: [], yaEstaban, sinLugar };

  const idsNuevos = entran.map((p) => p.varianteId);
  const antes = plan.productos.map((p) => p.varianteId);
  const destino = tomaIds ? new Set(tomaIds) : null;
  const tomas = plan.tomas.map((t) => {
    const va = !destino || destino.has(t.id);
    if (t.aplicaA === 'todos') return va ? t : { ...t, aplicaA: antes };
    return va ? { ...t, aplicaA: [...t.aplicaA, ...idsNuevos] } : t;
  });
  const productos = [
    ...plan.productos,
    ...planVacio(entran, [], '').productos,
  ];
  return { plan: sincronizarItems({ ...plan, tomas, productos }), sumados: idsNuevos, yaEstaban, sinLugar };
}

/** Lo que el escritor recibe del producto: medidas (si completas) y la ficha, como clave/valor. */
export function datosDelProducto(p: ProductoDelPlan): DatoDeProducto[] {
  const out: DatoDeProducto[] = [];
  if (tieneMedidasCompletas(p.medidas)) {
    const m = resumenMedidas(p.medidas);
    if (m) out.push({ clave: 'medidas', valor: m });
  }
  // Una característica suelta ("Glitter") viaja con una clave genérica: el escritor lee "clave: valor".
  for (const c of p.ficha.campos) out.push({ clave: c.clave || 'característica', valor: c.valor });
  return out;
}

const tomaPorId = (plan: PlanDeCatalogo): Map<string, TomaDelPlan> => new Map(plan.tomas.map((t) => [t.id, t]));

/** Qué impide generar, en el orden de la pantalla. Vacío = se puede. */
export function queFalta(plan: PlanDeCatalogo): string[] {
  const faltas: string[] = [];
  if (plan.productos.length === 0) faltas.push('ningún producto');
  if (plan.tomas.length === 0) faltas.push('ninguna toma');
  const tomas = tomaPorId(plan);
  for (const p of plan.productos) {
    if (tieneMedidasCompletas(p.medidas)) continue;
    const exige = p.items.map((i) => tomas.get(i.tomaId)).find((t) => t?.icono === 'medidas' || esInfografia(t));
    if (exige) faltas.push(`${p.nombre}: faltan las medidas (toma ${exige.titulo})`);
  }
  const sinPrompt = plan.productos.reduce(
    // Una toma con foto y sin prompt (planes de antes de que el estilo dejara de vaciarlas) no
    // traba Generar: su foto sigue y Generar lanza lo demás.
    (acc, p) => acc + p.items.filter((i) => (i.estado === 'sin-prompt' || i.estado === 'fallo' || !i.prompt.trim()) && !fotoALaVista(i)).length,
    0,
  );
  if (sinPrompt > 0) faltas.push(`${sinPrompt} prompt${sinPrompt === 1 ? '' : 's'} sin escribir`);
  return faltas;
}

export function totalImagenes(plan: PlanDeCatalogo): number {
  const tomas = tomaPorId(plan);
  return plan.productos.reduce((acc, p) => acc + p.items.reduce((a, i) => a + (tomas.get(i.tomaId)?.cantidad ?? 1), 0), 0);
}

export const costoDelPlan = (plan: PlanDeCatalogo): number => totalImagenes(plan) * creditosPorResolucion(plan.resolucion);

/** Una edición lista para salir: hay foto base y la base sigue siendo una de sus fotos. */
const editable = (i: ItemDelPlan): boolean => !!i.edicion && !!i.imageIds?.includes(i.edicion.baseImageId);

/**
 * Un ítem que Generar va a lanzar: con prompt y sin estar generando; lo ya generado sólo si
 * tiene una edición o un rehacer pendiente (review 2026-10-14: nunca relanzar lo lanzado).
 */
const esLanzable = (i: ItemDelPlan): boolean =>
  !!i.prompt.trim() && i.estado !== 'generando' && (i.estado !== 'generada' || !!i.rehacer || editable(i));

/** Cuántas imágenes lanza un ítem: una edición es una (la foto de la tarjeta); lo demás, la cantidad de la toma. */
const imagenesDelItem = (i: ItemDelPlan, toma: TomaDelPlan | undefined): number => (i.estado === 'generada' && !i.rehacer && editable(i) ? 1 : (toma?.cantidad ?? 1));

/** Las imágenes que un click en Generar lanza ahora. */
export function imagenesPorLanzar(plan: PlanDeCatalogo): number {
  const tomas = tomaPorId(plan);
  return plan.productos.reduce((acc, p) => acc + p.items.filter(esLanzable).reduce((a, i) => a + imagenesDelItem(i, tomas.get(i.tomaId)), 0), 0);
}

export const costoPorLanzar = (plan: PlanDeCatalogo): number => imagenesPorLanzar(plan) * creditosPorResolucion(plan.resolucion);

/** Las imágenes que ya están corriendo (tras recargar con un lote en vuelo, por ejemplo). */
export function imagenesEnCurso(plan: PlanDeCatalogo): number {
  const tomas = tomaPorId(plan);
  return plan.productos.reduce((acc, p) => acc + p.items.filter((i) => i.estado === 'generando').reduce((a, i) => a + (tomas.get(i.tomaId)?.cantidad ?? 1), 0), 0);
}

/** Un ítem cuyo prompt deja de valer: vuelve a "sin prompt" CONSERVANDO su foto (se ve hasta que
 *  salga la nueva) y sin lo pendiente, que era sobre el prompt viejo. */
export const itemSinPrompt = (i: ItemDelPlan): ItemDelPlan => {
  const { edicion: _e, rehacer: _r, ...resto } = i;
  return { ...resto, prompt: '', estado: 'sin-prompt', motivo: undefined };
};

/** El estilo común cambió: lo escrito y no fijado vuelve a "sin prompt" (lo que está generando se deja). */
export function invalidarPrompts(plan: PlanDeCatalogo): PlanDeCatalogo {
  return {
    ...plan,
    productos: plan.productos.map((p) => ({
      ...p,
      items: p.items.map((i) => (seInvalida(i) ? itemSinPrompt(i) : i)),
    })),
  };
}

/** Una toma con foto ya salida: un cambio de estilo o de toma no la reescribe sola
 *  (ADR-2026-10-15-una-toma-con-foto-no-pierde-su-prompt). Vaciarle el prompt la dejaba "sin
 *  prompt" y trababa Generar para todo; reescribirla cuesta regenerarla, así que se pide. */
const fotoALaVista = (i: ItemDelPlan): boolean => (i.imageIds?.length ?? 0) > 0;

/** Lo que un cambio de estilo o de toma vuelve a "sin prompt": lo escrito sin foto, no fijado, que no está generando. */
const seInvalida = (i: ItemDelPlan): boolean => !i.fijado && i.estado !== 'generando' && !fotoALaVista(i);

/** Lo que dice una toma cambió: sus prompts que se invalidan vuelven a "sin prompt". */
export function invalidarToma(plan: PlanDeCatalogo, tomaId: string): PlanDeCatalogo {
  return {
    ...plan,
    productos: plan.productos.map((p) => ({
      ...p,
      items: p.items.map((i) => (i.tomaId === tomaId && seInvalida(i) ? itemSinPrompt(i) : i)),
    })),
  };
}

/** Las etiquetas de un producto: hasta 4 textos cortos, sin vacíos ni repetidos. */
export function normalizarEtiquetas(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    const t = typeof v === 'string' ? v.trim().slice(0, MAX_LARGO_ETIQUETA) : '';
    if (t && !out.includes(t)) out.push(t);
  }
  return out.slice(0, MAX_PILLS_DE_INFOGRAFIA);
}

/** Cambiaron las etiquetas de un producto: sus fichas de medidas escritas (sin foto, no fijadas) vuelven a "sin prompt". */
export function invalidarInfografiasDelProducto(plan: PlanDeCatalogo, varianteId: string): PlanDeCatalogo {
  const infografias = new Set(plan.tomas.filter((t) => t.infografia).map((t) => t.id));
  return {
    ...plan,
    productos: plan.productos.map((p) =>
      p.varianteId !== varianteId ? p : { ...p, items: p.items.map((i) => (infografias.has(i.tomaId) && seInvalida(i) ? itemSinPrompt(i) : i)) },
    ),
  };
}

/** Las tomas con foto (no fijadas, no generando): las que un cambio de estilo o de tomas deja como estaban. */
export const tomasConFoto = (plan: PlanDeCatalogo): number =>
  plan.productos.reduce((acc, p) => acc + p.items.filter((i) => !i.fijado && i.estado !== 'generando' && fotoALaVista(i)).length, 0);

export interface FiltroDeItems {
  items?: string[];
  productos?: string[];
  tomas?: string[];
}

/**
 * Sin filtro: los ítems sin prompt o con fallo, no fijados. Con filtro: los pedidos (por id, por
 * producto o por toma) aunque ya estén escritos, siempre que no estén fijados.
 */
export function itemsPendientesDeEscritura(plan: PlanDeCatalogo, solo?: FiltroDeItems): ItemDelPlan[] {
  const hayFiltro = !!solo && ((solo.items?.length ?? 0) > 0 || (solo.productos?.length ?? 0) > 0 || (solo.tomas?.length ?? 0) > 0);
  const out: ItemDelPlan[] = [];
  for (const p of plan.productos) {
    for (const i of p.items) {
      if (i.fijado) continue;
      if (!hayFiltro) {
        if (i.estado === 'sin-prompt' || i.estado === 'fallo' || !i.prompt.trim()) out.push(i);
        continue;
      }
      const pedido =
        (solo!.items?.includes(i.id) ?? false) ||
        (solo!.productos?.includes(p.varianteId) ?? false) ||
        (solo!.tomas?.includes(i.tomaId) ?? false);
      if (pedido) out.push(i);
    }
  }
  return out;
}

/** Un ítem cuya tarjeta ya muestra una foto (aunque sea del prompt anterior). */
export const muestraFoto = (i: ItemDelPlan): boolean => (i.imageIds?.length ?? 0) > 0 && i.estado !== 'generando';

/**
 * Lo que `escribir_prompts` reescribiría, separado por si la tarjeta ya tiene foto
 * (ADR-2026-10-15-reescribir-una-toma-con-foto-es-explicito). Reescribir una toma con foto la
 * deja para regenerar —Generar la vuelve a hacer y cobra—, así que eso tiene que ser una
 * decisión y no un efecto de "cargá estos datos". `creditos`: lo que cuesta regenerar las que
 * tienen foto.
 */
export function aReescribir(plan: PlanDeCatalogo, solo?: FiltroDeItems): { sinFoto: ItemDelPlan[]; conFoto: ItemDelPlan[]; creditos: number } {
  const pedidos = itemsPendientesDeEscritura(plan, solo);
  const conFoto = pedidos.filter(muestraFoto);
  const tomas = tomaPorId(plan);
  const imagenes = conFoto.reduce((a, i) => a + (tomas.get(i.tomaId)?.cantidad ?? 1), 0);
  return { sinFoto: pedidos.filter((i) => !muestraFoto(i)), conFoto, creditos: imagenes * creditosPorResolucion(plan.resolucion) };
}

/** Sin filtro, todos los ítems; con filtro, los pedidos por id, producto o toma. */
export function itemsDelFiltro(plan: PlanDeCatalogo, solo?: FiltroDeItems): ItemDelPlan[] {
  const hayFiltro = !!solo && ((solo.items?.length ?? 0) > 0 || (solo.productos?.length ?? 0) > 0 || (solo.tomas?.length ?? 0) > 0);
  return plan.productos.flatMap((p) =>
    p.items.filter(
      (i) =>
        !hayFiltro ||
        (solo!.items?.includes(i.id) ?? false) ||
        (solo!.productos?.includes(p.varianteId) ?? false) ||
        (solo!.tomas?.includes(i.tomaId) ?? false),
    ),
  );
}

const conLosItems = (plan: PlanDeCatalogo, ids: ReadonlySet<string>, f: (i: ItemDelPlan) => ItemDelPlan): PlanDeCatalogo => ({
  ...plan,
  productos: plan.productos.map((p) => ({ ...p, items: p.items.map((i) => (ids.has(i.id) ? f(i) : i)) })),
});

const tieneFoto = (i: ItemDelPlan): boolean => i.estado === 'generada' && (i.imageIds?.length ?? 0) > 0;

/**
 * Deja una edición pendiente en las fotos ya salidas del filtro (sin filtro, todas): la base es la
 * foto de la tarjeta. No lanza nada: Generar la manda, con su costo a la vista
 * (ADR-2026-10-14-el-agente-edita-el-catalogo-en-masa).
 */
export function marcarEdicion(
  plan: PlanDeCatalogo,
  solo: FiltroDeItems | undefined,
  instruccion: string,
): { plan: PlanDeCatalogo; marcados: string[]; sinFoto: number } {
  const texto = instruccion.trim().slice(0, MAX_INSTRUCCION_EDICION);
  const elegidos = itemsDelFiltro(plan, solo);
  if (!texto) return { plan, marcados: [], sinFoto: 0 };
  const conFoto = elegidos.filter(tieneFoto);
  const ids = new Set(conFoto.map((i) => i.id));
  const nuevo = conLosItems(plan, ids, (i) => {
    const { rehacer: _r, ...resto } = i;
    return { ...resto, edicion: { instruccion: texto, baseImageId: i.imageIds![0] } };
  });
  return { plan: nuevo, marcados: conFoto.map((i) => i.id), sinFoto: elegidos.length - conFoto.length };
}

/** Deja un rehacer pendiente en las fotos ya salidas del filtro: Generar las vuelve a generar con su prompt. */
export function marcarRehacer(plan: PlanDeCatalogo, solo: FiltroDeItems | undefined): { plan: PlanDeCatalogo; marcados: string[] } {
  const conFoto = itemsDelFiltro(plan, solo).filter((i) => tieneFoto(i) && !!i.prompt.trim());
  const ids = new Set(conFoto.map((i) => i.id));
  const nuevo = conLosItems(plan, ids, (i) => {
    const { edicion: _e, ...resto } = i;
    return { ...resto, rehacer: true };
  });
  return { plan: nuevo, marcados: conFoto.map((i) => i.id) };
}

/** Saca las ediciones y los rehacer pendientes del filtro (sin filtro, de todo el plan). */
export function quitarPendientes(plan: PlanDeCatalogo, solo?: FiltroDeItems): PlanDeCatalogo {
  const ids = new Set(itemsDelFiltro(plan, solo).filter((i) => i.edicion || i.rehacer).map((i) => i.id));
  if (ids.size === 0) return plan;
  return conLosItems(plan, ids, (i) => {
    const { edicion: _e, rehacer: _r, ...resto } = i;
    return resto;
  });
}

export interface ReferenciaDeLote {
  tipo: 'producto' | 'adjunto' | 'generada';
  product_id?: string;
  url?: string;
  etiqueta?: string;
}

export interface ItemDeLoteArmado {
  id: string;
  etiqueta: string;
  prompt: string;
  cantidad: number;
  reference_images: ReferenciaDeLote[];
  tarea?: TareaDeImagen;
  piel_expuesta?: boolean;
  fondo_blanco?: boolean;
  fondo_blanco_iluminacion?: 'difusa' | 'dura';
}

export interface LoteArmado {
  items: ItemDeLoteArmado[];
  /** Id del lote (`i7`) → id del ítem del plan. */
  itemIdPorLoteId: Record<string, string>;
  total: number;
  /** Los ítems del plan que este lote manda como edición. */
  ediciones: string[];
}

/**
 * Los ítems con prompt → lotes de hasta `MAX_IMAGENES_POR_LOTE` imágenes, con ids cortos (`i0`,
 * `i1`…) que respetan el alfabeto del backend (los del plan son uuids y pasan los 40). El producto
 * viaja siempre como referencia; las referencias del plan que la toma lista, firmadas por
 * `urlDeReferencia` (null = se omite). Una edición pendiente va como UNA imagen con la foto de
 * la tarjeta, firmada por `urlDeGenerada`, como base (sin firma, no sale); con `soloItems`
 * (Rehacer de la tarjeta) va el prompt de siempre.
 */
export function armarLotes(
  plan: PlanDeCatalogo,
  urlDeReferencia: (r: ReferenciaDelPlan) => string | null,
  soloItems?: string[],
  urlDeGenerada: (imageId: string) => string | null = () => null,
): LoteArmado[] {
  const tomas = tomaPorId(plan);
  const referencias = new Map(plan.referencias.map((r) => [r.id, r]));
  const lotes: LoteArmado[] = [];
  const nuevoLote = (): LoteArmado => ({ items: [], itemIdPorLoteId: {}, total: 0, ediciones: [] });
  let actual = nuevoLote();
  let n = 0;
  const cerrar = () => {
    if (actual.items.length > 0) lotes.push(actual);
    actual = nuevoLote();
  };
  for (const p of plan.productos) {
    for (const it of p.items) {
      if (soloItems && !soloItems.includes(it.id)) continue;
      // Lo que ya corre no se relanza nunca; lo generado sólo a pedido (Rehacer, o una edición o
      // un rehacer pendiente).
      if (it.estado === 'generando') continue;
      const toma = tomas.get(it.tomaId);
      if (!toma || !it.prompt.trim()) continue;
      const comoEdicion = !soloItems && it.estado === 'generada' && !it.rehacer && editable(it);
      if (it.estado === 'generada' && !soloItems && !it.rehacer && !comoEdicion) continue;
      if (comoEdicion) {
        const url = urlDeGenerada(it.edicion!.baseImageId);
        if (!url) continue;
        if (actual.total + 1 > MAX_IMAGENES_POR_LOTE) cerrar();
        const id = `i${n++}`;
        actual.items.push({
          id,
          etiqueta: `${p.nombre} · ${toma.titulo} (edición)`,
          prompt: promptDeEdicion(it.edicion!.instruccion),
          cantidad: 1,
          reference_images: [
            { tipo: 'generada', url, etiqueta: 'La foto a editar' },
            { tipo: 'producto', product_id: p.varianteId },
          ],
          tarea: 'editar',
          ...(it.pielExpuesta ? { piel_expuesta: true } : {}),
          ...(toma.fondoBlanco ? { fondo_blanco: true } : {}),
          ...(toma.fondoBlanco && toma.luzFondoBlanco ? { fondo_blanco_iluminacion: toma.luzFondoBlanco } : {}),
        });
        actual.itemIdPorLoteId[id] = it.id;
        actual.ediciones.push(it.id);
        actual.total += 1;
        continue;
      }
      if (actual.total + toma.cantidad > MAX_IMAGENES_POR_LOTE) cerrar();
      const id = `i${n++}`;
      const extras: ReferenciaDeLote[] = [];
      for (const rid of toma.referencias ?? []) {
        const r = referencias.get(rid);
        const url = r ? urlDeReferencia(r) : null;
        if (r && url) extras.push({ tipo: 'adjunto', url, etiqueta: r.etiqueta });
      }
      actual.items.push({
        id,
        etiqueta: `${p.nombre} · ${toma.titulo}`,
        prompt: it.prompt,
        cantidad: toma.cantidad,
        reference_images: [{ tipo: 'producto', product_id: p.varianteId }, ...extras],
        ...(it.tarea ?? toma.tarea ? { tarea: it.tarea ?? toma.tarea } : {}),
        ...(it.pielExpuesta ? { piel_expuesta: true } : {}),
        ...(toma.fondoBlanco ? { fondo_blanco: true } : {}),
        ...(toma.fondoBlanco && toma.luzFondoBlanco ? { fondo_blanco_iluminacion: toma.luzFondoBlanco } : {}),
      });
      actual.itemIdPorLoteId[id] = it.id;
      actual.total += toma.cantidad;
    }
  }
  cerrar();
  return lotes;
}

const conItems = (plan: PlanDeCatalogo, f: (i: ItemDelPlan, p: ProductoDelPlan) => ItemDelPlan): PlanDeCatalogo => ({
  ...plan,
  productos: plan.productos.map((p) => ({ ...p, items: p.items.map((i) => f(i, p)) })),
});

/** Lo que respondió `/v1/agent/lote`: cada ítem pasa a `generando` con su `executionId`; el lote se anota. */
export function marcarLanzados(
  plan: PlanDeCatalogo,
  lote: LoteArmado,
  respuesta: { job_id: string; items: Array<{ id: string; execution_id: string }> },
): PlanDeCatalogo {
  const ejecucionPorItem = new Map<string, string>();
  for (const r of respuesta.items) {
    const itemId = lote.itemIdPorLoteId[r.id];
    if (itemId) ejecucionPorItem.set(itemId, r.execution_id);
  }
  const ediciones = new Set(lote.ediciones);
  const ahora = Date.now();
  // La foto que había se queda a la vista mientras sale la nueva, y vuelve si la nueva no sale.
  const marcado = conItems(plan, (i) => {
    const e = ejecucionPorItem.get(i.id);
    if (!e) return i;
    const { edicion: _e, rehacer: _r, fueEdicion: _f, previas: _p, ...resto } = i;
    const previas = i.imageIds ?? [];
    return {
      ...resto,
      estado: 'generando',
      executionId: e,
      imageIds: previas,
      motivo: undefined,
      lanzadoEn: ahora,
      ...(previas.length ? { previas } : {}),
      ...(ediciones.has(i.id) ? { fueEdicion: true as const } : {}),
    };
  });
  const itemIds = Object.values(lote.itemIdPorLoteId).filter((id) => ejecucionPorItem.has(id));
  return {
    ...marcado,
    lotes: [
      ...plan.lotes,
      { jobId: respuesta.job_id, executionIds: itemIds.map((id) => ejecucionPorItem.get(id)!), itemIds, total: lote.total, terminado: false },
    ],
  };
}

/** Las filas de `imagenes_generadas` que llegaron → el ítem de cada una, por `execution_id`. */
export function aplicarFilas(
  plan: PlanDeCatalogo,
  filas: Array<{ id: string; execution_id?: string | null; has_error: boolean }>,
): PlanDeCatalogo {
  if (filas.length === 0) return plan;
  const okPorEjecucion = new Map<string, string[]>();
  const fallos = new Set<string>();
  for (const f of filas) {
    if (!f.execution_id) continue;
    if (f.has_error) {
      fallos.add(f.execution_id);
      continue;
    }
    const lista = okPorEjecucion.get(f.execution_id) ?? [];
    lista.push(f.id);
    okPorEjecucion.set(f.execution_id, lista);
  }
  return conItems(plan, (i) => {
    if (!i.executionId) return i;
    const ok = okPorEjecucion.get(i.executionId);
    // Una edición reemplaza sólo la foto de la tarjeta; lo demás, todas.
    if (ok && ok.length > 0) return { ...i, estado: 'generada', imageIds: i.fueEdicion ? [...ok, ...(i.previas ?? []).slice(1)] : ok, motivo: undefined };
    if (fallos.has(i.executionId)) {
      if (i.previas?.length) {
        return { ...i, estado: 'generada', imageIds: i.previas, motivo: i.fueEdicion ? 'la edición no salió; no se cobró' : 'la nueva no salió; no se cobró' };
      }
      return { ...i, estado: 'fallo', motivo: 'la imagen no salió; no se cobró' };
    }
    return i;
  });
}

/** Pasado esto desde que se lanzó, una toma que sigue sin fila no va a llegar: el backend escribe
 *  la fila al terminar o al fallar, y una tanda grande no tarda tanto. Es el doble del poll más largo. */
export const TOMA_VENCIDA_MS = 30 * 60_000;

/**
 * El poll de un lote terminó (llegó todo, se cortó por tiempo, por un hueco largo o porque se
 * salió de la pantalla). El lote se da por terminado SÓLO si no le queda nada generando: si no,
 * queda abierto y `reanudarLotes` lo vuelve a polear al abrir la sesión, así una foto que llega
 * tarde todavía cae en su toma. Una toma que pasó `TOMA_VENCIDA_MS` sin fila se suelta (vuelve
 * su foto anterior, o queda en fallo) para que Generar pueda relanzarla. Puro.
 */
export function cerrarLote(plan: PlanDeCatalogo, jobId: string, ahora = Date.now()): PlanDeCatalogo {
  const lote = plan.lotes.find((l) => l.jobId === jobId);
  if (!lote) return plan;
  const delLote = new Set(lote.executionIds);
  let quedan = false;
  const soltado = conItems(plan, (i) => {
    if (i.estado !== 'generando' || !i.executionId || !delLote.has(i.executionId)) return i;
    if (ahora - (i.lanzadoEn ?? 0) < TOMA_VENCIDA_MS) {
      quedan = true;
      return i;
    }
    return i.previas?.length
      ? { ...i, estado: 'generada' as const, imageIds: i.previas, motivo: 'la nueva no llegó' }
      : { ...i, estado: 'fallo' as const, motivo: 'la imagen no llegó' };
  });
  return { ...soltado, lotes: plan.lotes.map((l) => (l.jobId === jobId ? { ...l, terminado: !quedan } : l)) };
}

// ── Normalizar lo guardado ──────────────────────────────────────────────────────

const str = (v: unknown, max = 10_000): string => (typeof v === 'string' ? v.slice(0, max) : '');

/**
 * Lo más largo que puede ser el prompt de una toma: el mismo tope que el lote del backend
 * (`MAX_PROMPT_DE_IMAGEN`). La ficha de medidas es la plantilla del catálogo viejo y pasa los
 * 8.000 con las pills; antes este tope la cortaba en silencio al recargar el plan
 * (ADR-2026-10-19-el-prompt-de-la-ficha-de-medidas-no-entraba-en-el-lote).
 */
export const MAX_PROMPT_DE_TOMA = 20_000;
const esIcono = (v: unknown): v is IconoDeToma => typeof v === 'string' && (ICONOS_DE_TOMA as readonly string[]).includes(v);
const esTarea = (v: unknown): v is TareaDeImagen => typeof v === 'string' && (TAREAS_DE_IMAGEN as readonly string[]).includes(v);
const cantidadDeToma = (v: unknown): 1 | 2 | 3 => {
  const n = typeof v === 'number' ? Math.round(v) : 1;
  return (n < 1 ? 1 : n > MAX_CANTIDAD_TOMA ? MAX_CANTIDAD_TOMA : n) as 1 | 2 | 3;
};
const idKebab = (v: unknown): string => {
  const s = str(v).trim().toLowerCase();
  return /^[a-z0-9-]{1,40}$/.test(s) ? s : '';
};
const listaDeStrings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : []);

export function normalizarToma(raw: unknown): TomaDelPlan | null {
  const o = (raw ?? {}) as Record<string, unknown>;
  const id = idKebab(o.id);
  const titulo = str(o.titulo, MAX_TITULO_TOMA).trim();
  if (!id || !titulo) return null;
  const aplicaA = o.aplicaA === 'todos' || o.aplicaA === undefined ? 'todos' : listaDeStrings(o.aplicaA);
  return {
    id,
    titulo,
    icono: esIcono(o.icono) ? o.icono : 'otro',
    queMuestra: str(o.queMuestra, MAX_QUE_MUESTRA).trim(),
    aplicaA: aplicaA === 'todos' ? 'todos' : aplicaA,
    cantidad: cantidadDeToma(o.cantidad),
    ...(o.fondoBlanco === true ? { fondoBlanco: true } : {}),
    ...(o.luzFondoBlanco === 'dura' || o.luzFondoBlanco === 'difusa' ? { luzFondoBlanco: o.luzFondoBlanco } : {}),
    ...(esTarea(o.tarea) ? { tarea: o.tarea } : {}),
    ...(Array.isArray(o.referencias) && o.referencias.length > 0 ? { referencias: listaDeStrings(o.referencias) } : {}),
    ...(o.infografia === true ? { infografia: true as const } : {}),
    ...(o.pillsDeLaFicha === false ? { pillsDeLaFicha: false } : {}),
    ...(esColorDeInfografia(o.colorInfografia) ? { colorInfografia: o.colorInfografia } : {}),
    ...(typeof o.estiloGrafico === 'string' && o.estiloGrafico.trim() ? { estiloGrafico: o.estiloGrafico.trim().slice(0, 600) } : {}),
  };
}

const esColorDeInfografia = (v: unknown): v is ColorDeInfografia => typeof v === 'string' && (COLORES_DE_INFOGRAFIA as readonly string[]).includes(v);

function normalizarItem(raw: unknown, varianteId: string): ItemDelPlan | null {
  const o = (raw ?? {}) as Record<string, unknown>;
  const tomaId = idKebab(o.tomaId);
  if (!tomaId) return null;
  const estado = ESTADOS.includes(o.estado as EstadoDeItem) ? (o.estado as EstadoDeItem) : 'sin-prompt';
  return {
    id: idDeItem(varianteId, tomaId),
    tomaId,
    prompt: str(o.prompt, MAX_PROMPT_DE_TOMA),
    fijado: o.fijado === true,
    estado,
    ...(typeof o.motivo === 'string' ? { motivo: o.motivo } : {}),
    ...(typeof o.executionId === 'string' ? { executionId: o.executionId } : {}),
    ...(Array.isArray(o.imageIds) ? { imageIds: listaDeStrings(o.imageIds) } : {}),
    ...(esTarea(o.tarea) ? { tarea: o.tarea } : {}),
    ...(o.pielExpuesta === true ? { pielExpuesta: true } : {}),
    ...(edicionGuardada(o.edicion) ? { edicion: edicionGuardada(o.edicion)! } : {}),
    ...(o.rehacer === true ? { rehacer: true as const } : {}),
    ...(Array.isArray(o.previas) && o.previas.length > 0 ? { previas: listaDeStrings(o.previas) } : {}),
    ...(o.fueEdicion === true ? { fueEdicion: true as const } : {}),
    ...(typeof o.lanzadoEn === 'number' && Number.isFinite(o.lanzadoEn) ? { lanzadoEn: o.lanzadoEn } : {}),
  };
}

function edicionGuardada(raw: unknown): EdicionPendiente | null {
  const o = (raw ?? null) as Record<string, unknown> | null;
  if (!o || typeof o !== 'object') return null;
  const instruccion = str(o.instruccion, MAX_INSTRUCCION_EDICION).trim();
  const baseImageId = str(o.baseImageId).trim();
  return instruccion && baseImageId ? { instruccion, baseImageId } : null;
}

function normalizarProducto(raw: unknown): ProductoDelPlan | null {
  const o = (raw ?? {}) as Record<string, unknown>;
  const varianteId = str(o.varianteId).trim();
  if (!varianteId) return null;
  return {
    varianteId,
    nombre: str(o.nombre, 120).trim() || 'Producto',
    ...(typeof o.portadaUrl === 'string' ? { portadaUrl: o.portadaUrl } : {}),
    medidas: normalizarMedidas(o.medidas),
    ficha: normalizarFicha(o.ficha),
    ...(typeof o.notas === 'string' && o.notas.trim() ? { notas: o.notas.slice(0, 600) } : {}),
    ...(normalizarEtiquetas(o.etiquetas).length ? { etiquetas: normalizarEtiquetas(o.etiquetas) } : {}),
    items: (Array.isArray(o.items) ? o.items : []).map((i) => normalizarItem(i, varianteId)).filter((i): i is ItemDelPlan => i !== null),
  };
}

function normalizarLote(raw: unknown): LoteLanzado | null {
  const o = (raw ?? {}) as Record<string, unknown>;
  const jobId = str(o.jobId).trim();
  if (!jobId) return null;
  return {
    jobId,
    executionIds: listaDeStrings(o.executionIds),
    itemIds: listaDeStrings(o.itemIds),
    total: typeof o.total === 'number' ? o.total : 0,
    terminado: o.terminado === true,
  };
}

/** Lo que vuelve de `lienzo.catalogo` → un plan saneado, o null si no es un plan. */
export function normalizarPlan(raw: unknown): PlanDeCatalogo | null {
  const o = raw as Record<string, unknown> | null;
  if (!o || typeof o !== 'object' || o.version !== 1 || !Array.isArray(o.productos) || !Array.isArray(o.tomas)) return null;
  const formato = FORMAT_OPTIONS_V2.some((f) => f.id === o.formato) ? (o.formato as string) : DEFAULT_FORMAT_ID;
  const plan: PlanDeCatalogo = {
    version: 1,
    titulo: str(o.titulo, 80).trim() || tituloDeHoy(),
    pedido: str(o.pedido, 4_000),
    estilo: str(o.estilo, MAX_ESTILO),
    formato,
    resolucion: normalizarResolucion(typeof o.resolucion === 'string' ? o.resolucion : null),
    tomas: o.tomas.map(normalizarToma).filter((t): t is TomaDelPlan => t !== null).slice(0, MAX_TOMAS),
    productos: o.productos.map(normalizarProducto).filter((p): p is ProductoDelPlan => p !== null).slice(0, MAX_PRODUCTOS),
    referencias: (Array.isArray(o.referencias) ? o.referencias : [])
      .map((r) => {
        const x = (r ?? {}) as Record<string, unknown>;
        const id = str(x.id).trim();
        const storagePath = str(x.storagePath).trim();
        return id && storagePath
          ? { id, etiqueta: str(x.etiqueta, 120).trim() || 'Referencia', storagePath, ...(typeof x.bucket === 'string' ? { bucket: x.bucket } : {}) }
          : null;
      })
      .filter((r): r is ReferenciaDelPlan => r !== null),
    lotes: (Array.isArray(o.lotes) ? o.lotes : []).map(normalizarLote).filter((l): l is LoteLanzado => l !== null),
    ...(o.pedidoEnviado === true ? { pedidoEnviado: true } : {}),
  };
  return sincronizarItems(plan);
}
